import {useMutation} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'

import {useLayoutQuery, useLayoutQueryKey} from '../page-data/loaders/use-layout-query'
import {produce} from 'immer'

type UpdateSubscriptionCallbacks = {
  onSuccess?: (subscribed: boolean) => void
  onError?: (error: Error) => void
}

/**
 * Subscribes or unsubscribes the viewer to/from this pull request's notification thread
 * via the classic `/notifications/thread` endpoint (the same one backing Issues and Commits).
 *
 * The classic notifications system tracks pull request subscriptions against the pull
 * request's backing Issue rather than the PullRequest record itself, so this posts
 * `thread_class: 'Issue'` with `notificationSubscriptionThreadId` (the Issue's database id),
 * not the pull request's own id.
 */
export function useUpdateSubscriptionMutation({onSuccess, onError}: UpdateSubscriptionCallbacks = {}) {
  const {repository, notificationSubscriptionThreadId} = useLayoutQuery().data
  const layoutQueryKey = useLayoutQueryKey()

  return useMutation({
    mutationFn: async (subscribe: boolean) => {
      const formData = new FormData()
      formData.append('repository_id', repository.id.toString())
      formData.append('thread_id', (notificationSubscriptionThreadId ?? '').toString())
      formData.append('thread_class', 'Issue')
      formData.append('id', subscribe ? 'subscribe' : 'unsubscribe')
      const response = await verifiedFetch('/notifications/thread', {method: 'POST', body: formData})
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    },
    onSuccess: (_response, subscribed, _, context) => {
      context.client.setQueryData(
        layoutQueryKey,
        data =>
          data &&
          produce(data, draft => {
            draft.payload.user.subscribed = subscribed
          }),
      )
      onSuccess?.(subscribed)
    },
    onError,
  })
}
